import React, { useEffect, useState, useContext } from "react";
import Search from "../../components/Search";
import FilterDropDown from "../../components/FilterDropDown";
import CreateActivityButton from "../../components/buttons/CreateActivityButton";
import UserBadge from "../../components/buttons/UserBadge";
import BackButton from "../../components/buttons/BackButton";
import ActivityTabBar from "../../components/ActivityTabBar";
import api from "../../Api";
import iconId from "../../assets/icon/arrow/freccia.png";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";

import icon1 from "../../assets/icon/pin/Pin/pinArancione.png";
import icon2 from "../../assets/icon/pin/Pin/pinBlu.png";
import icon3 from "../../assets/icon/pin/Pin/pinGiallo.png";
import icon4 from "../../assets/icon/pin/Pin/pinRosa.png";
import icon5 from "../../assets/icon/pin/Pin/pinTurchese.png";
import icon6 from "../../assets/icon/pin/Pin/pinVerde.png";
import icon7 from "../../assets/icon/pin/Pin/pinViola.png";
import icon8 from "../../assets/icon/pin/Pin/PinRosso.png";

export default function AllActivity() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const isRup = user?.role === "rup"; // flag di comodo
  const canDelete = isRup;            // se vuoi abilitare anche altri ruoli gestiscilo qui

  const [activities, setActivities] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  // selezione / cancellazione
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const itemsPerPage = 20;


  useEffect(() => {
    const fetchActivities = async () => {
      try {
        let endpoint, params = {};
        if (user?.role === "rup") {
          endpoint = "/activity/worksite-activities";
          params.worksite = user.worksite;
        } else {
          endpoint = "/activity/activities";
        }
        const res = await api.get(endpoint, { params });
        const userCache = {};

        const enriched = await Promise.all(
          res.data.map(async (act) => {
            let operatorName = "N/A",
              role = "-";
            if (act.operator_id) {
              if (userCache[act.operator_id]) {
                ({ firstName: operatorName, lastName: role } =
                  userCache[act.operator_id]);
              } else {
                try {
                  const u = (await api.get(`/user/${act.operator_id}`)).data;
                  userCache[act.operator_id] = u;
                  operatorName = `${u.firstName} ${u.lastName}`;
                  role = u.role || "-";
                } catch {}
              }
            }
            return { ...act, operatorName, role };
          })
        );

        enriched.sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
        setActivities(enriched);
      } catch (err) {
        console.error("Errore nel caricamento attività:", err);
      }
    };

    if (user) fetchActivities();
  }, [user]);

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAll = (checked, rows) =>
    setSelectedIds(checked ? new Set(rows.map((a) => a.id)) : new Set());


  const deleteActivities = async () => {
    if (!selectedIds.size) return;
    const ok = window.confirm(
      `Eliminare definitivamente ${selectedIds.size} attività?`
    );
    if (!ok) return;

    try {
      setIsDeleting(true);
      await Promise.all(
        [...selectedIds].map((id) => api.delete(`/activity/activities/${id}`))
      );
      setActivities((prev) => prev.filter((a) => !selectedIds.has(a.id)));
      setSelectedIds(new Set());
      toast.success("Attività eliminate con successo ✅");
    } catch (err) {
      console.error(err);
      toast.error("Errore nella cancellazione ❌");
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => setCurrentPage(1), [searchQuery, activeFilters, activeTab]);

  const updateActivityStatus = async (id, newStatus) => {
    try {
      await api.patch(`/activity/activities/${id}`, { status: newStatus });
      setActivities((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a))
      );
      toast.success(
        newStatus === "open"
          ? "Attività aperta con successo ✅"
          : "Attività chiusa con successo ✅"
      );
    } catch {
      toast.error("Errore durante l'aggiornamento dello stato ❌");
    }
  };

  const goToActivity = (id) => navigate(`/activity-details/${id}`);

  const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);
  const formatDateWithWeekday = (dateStr) => {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const icons = {
    Potatura: icon1,
    Abbattimento: icon2,
    "Reti di recinzione": icon3,
    Sfalcio: icon4,
    "Reti paramassi": icon5,
    Diserbo: icon6,
    "Censimento arboreo": icon7,
    "Pulizia strada": icon8,
  };
  const labelMap = {
    potatura: "Potatura",
    abbattimento: "Abbattimento",
    reti_recinzione: "Reti di recinzione",
    sfalcio: "Sfalcio",
    reti_paramassi: "Reti paramassi",
    diserbo: "Diserbo",
    censimento_arboreo: "Censimento arboreo",
    pulizia_strada: "Pulizia strada",
  };
  const getActivityIcon = (label) => icons[label] || null;
  const getLabelFromValue = (val) => labelMap[val] || val;
  const formatLocation = (loc, actName) => {
    if (!loc) return "-";
    const parts = loc.split(",").map((p) => p.trim());
    const icon = getActivityIcon(getLabelFromValue(actName));
    return (
      <span className="flex items-center gap-2">
        {icon && <img src={icon} alt="" className="w-5" />}
        <span>{`${parts[1] || ""} ${parts[0] || ""}, ${parts[4] || ""}`}</span>
      </span>
    );
  };


  const filtered = activities.filter((act) => {
    const matchesTipo =
      !activeFilters.length ||
      act.activity_names.some((name) =>
        activeFilters.includes(name.toLowerCase().replace(/\s/g, "_"))
      );
    const matchesSearch =
      !searchQuery.trim() ||
      (act.location_info &&
        act.location_info.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesTipo && matchesSearch;
  });

  const displayedActivities =
    activeTab === "censimento_arboreo"
      ? filtered.filter((act) =>
          act.activity_names
            .map((n) => n.toLowerCase().replace(/\s/g, "_"))
            .includes("censimento_arboreo")
        )
      : filtered;

  /* ------------------------------------------------------------------------ */
  /*                           PAGINAZIONE                                    */
  /* ------------------------------------------------------------------------ */
  const totalPages = Math.ceil(displayedActivities.length / itemsPerPage);
  const paginatedActivities = displayedActivities.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  /* ------------------------------------------------------------------------ */
  /*                         EXPORT (CSV / XLSX)                              */
  /* ------------------------------------------------------------------------ */
  const downloadCSV = () => {
    const headers = isRup
      ? ["Responsabile", "Ruolo", "Attività", "Location"]
      : ["Data lavoro", "Responsabile", "Ruolo", "Attività", "Location"];

    const rows = displayedActivities.map((act) =>
      isRup
        ? [
            act.operatorName,
            act.role,
            act.activity_names.join("; "),
            act.location_info,
          ]
        : [
            formatDateWithWeekday(act.end_date),
            act.operatorName,
            act.role,
            act.activity_names.join("; "),
            act.location_info,
          ]
    );

    const csv = [headers, ...rows]
      .map((r) => r.map((f) => `"${String(f).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "all_activities_report.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadExcel = () => {
    const data = displayedActivities.map((act) =>
      isRup
        ? {
            Responsabile: act.operatorName,
            Ruolo: act.role,
            Attività: act.activity_names.join("; "),
            Location: act.location_info,
          }
        : {
            "Data lavoro": formatDateWithWeekday(act.end_date),
            Responsabile: act.operatorName,
            Ruolo: act.role,
            Attività: act.activity_names.join("; "),
            Location: act.location_info,
          }
    );
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tutte le attività");
    XLSX.writeFile(wb, "all_activities_report.xlsx");
  };

  /* ------------------------------------------------------------------------ */
  /*                               RENDER                                     */
  /* ------------------------------------------------------------------------ */
  return (
    <div className="min-h-screen bg-global p-12">
      {/* ---------------------------- HEADER -------------------------------- */}
      <header className="flex items-center mb-6">
        <div className="flex space-x-2 items-center">
          <FilterDropDown onFilterChange={setActiveFilters} />
          <Search onSearch={setSearchQuery} />
        </div>

        <div className="flex-1 flex justify-center">
          <ActivityTabBar activeTab={activeTab} onTabChange={setActiveTab} />
        </div>

        <div className="flex space-x-2 items-center">
          <CreateActivityButton />
          <UserBadge />
          <BackButton />
        </div>
      </header>

      <div className="mb-6 flex gap-4 flex-wrap">
        <button
          onClick={downloadCSV}
          className="px-4 py-2 rounded-xl bg-primary border-2 border-secondary text-white"
        >
          Scarica CSV Report
        </button>
        <button
          onClick={downloadExcel}
          className="px-4 py-2 rounded-xl bg-primary border-2 border-secondary text-white"
        >
          Scarica Excel Report
        </button>

        {canDelete && selectedIds.size > 0 && (
          <button
            onClick={deleteActivities}
            disabled={isDeleting}
            className="px-4 py-2 rounded-xl bg-red-600 border-2 border-secondary text-white"
          >
            Elimina&nbsp;{selectedIds.size}&nbsp;attività
          </button>
        )}
      </div>

      <div className="mt-8">
        <h3 className="text-white text-xl font-semibold mb-4">
          {activeTab === "all" ? "Tutte le attività" : "Censimento arboreo"}
        </h3>

        <div className="w-full overflow-x-auto rounded-2xl">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-gray-400 uppercase text-xs tracking-wider">
                {canDelete && (
                  <th className="py-2 px-4">
                    <input
                      type="checkbox"
                      checked={
                        paginatedActivities.length > 0 &&
                        selectedIds.size === paginatedActivities.length
                      }
                      onChange={(e) =>
                        toggleSelectAll(e.target.checked, paginatedActivities)
                      }
                    />
                  </th>
                )}
                <th className="py-2 px-4">Data lavoro</th>
                <th className="py-2 px-4">Responsabile</th>
                <th className="py-2 px-4">Ruolo</th>
                <th className="py-2 px-4">Attività</th>
                <th className="py-2 px-4">Location</th>
                <th className="py-2 px-4">Azioni</th>
                <th className="py-2 px-4">Dettaglio</th>
              </tr>
            </thead>

            <tbody>
              {paginatedActivities.map((activity, idx) => {
                const bg = idx % 2 === 0 ? "bg-global" : "bg-primary";
                return (
                  <tr
                    key={activity.id}
                    className={`${bg} text-white hover:bg-white/5`}
                  >
                    {canDelete && (
                      <td className="py-6 px-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(activity.id)}
                          onChange={() => toggleSelect(activity.id)}
                        />
                      </td>
                    )}

                    <td className="py-6 px-4">
                      {formatDateWithWeekday(activity.end_date)}
                    </td>
                    <td className="py-6 px-4">{activity.operatorName}</td>
                    <td className="py-6 px-4">
                      <span className="border-2 border-secondary rounded-full p-2 px-6">
                        {activity.role}
                      </span>
                    </td>
                    <td className="py-6 px-4">
                      <div className="flex flex-col gap-2">
                        {activity.activity_names.map((name, i) => {
                          const label = getLabelFromValue(name);
                          const icon = getActivityIcon(label);
                          return (
                            <div key={i} className="flex items-center gap-4">
                              {icon && (
                                <img src={icon} alt={label} className="w-4" />
                              )}
                              <span className="text-sm">{label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className="py-6 px-4">
                      {formatLocation(
                        activity.location_info,
                        activity.activity_names[0]
                      )}
                    </td>
                    <td className="py-6 px-4">
                      {activity.status === "pending" && (
                        <button
                          onClick={() =>
                            updateActivityStatus(activity.id, "open")
                          }
                          className="bg-green-600 text-white px-3 py-1 rounded-full hover:bg-green-700"
                        >
                          Apri
                        </button>
                      )}
                      {activity.status === "open" && (
                        <button
                          onClick={() =>
                            updateActivityStatus(activity.id, "closed")
                          }
                          className="bg-green-600 text-white px-3 py-1 rounded-full hover:bg-green-700"
                        >
                          Chiudi
                        </button>
                      )}
                      {activity.status === "closed" && (
                        <span className="bg-gray-600 text-white px-3 py-1 rounded-full opacity-70">
                          Chiusa
                        </span>
                      )}
                    </td>
                    <td className="py-6 px-4">
                      <img
                        src={iconId}
                        alt="Dettaglio"
                        onClick={() => goToActivity(activity.id)}
                        className="w-5 h-5 opacity-80 hover:opacity-100 cursor-pointer"
                      />
                    </td>
                  </tr>
                );
              })}

              {paginatedActivities.length === 0 && (
                <tr className="bg-global text-white text-center">
                  <td
                    colSpan={canDelete ? 8 : 7}
                    className="py-6 px-4"
                  >
                    Nessuna attività corrispondente ai criteri di ricerca.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center mt-6 gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded bg-secondary text-white disabled:opacity-50"
            >
              ← Precedente
            </button>

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-4 py-2 rounded ${
                  currentPage === i + 1
                    ? "bg-green-500 text-black font-semibold"
                    : "bg-primary text-white"
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage((p) => Math.min(p + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded bg-secondary text-white disabled:opacity-50"
            >
              Successiva →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
